import React from 'react';
import ReactDOM from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import { App } from './app/App';
import { bootstrap } from './app/bootstrap';
import './styles/globals.css';

/**
 * Drive `--app-height` (used by #root in globals.css) from JS, because iOS
 * standalone PWAs report CSS viewport units ~59px short of the real screen and
 * unstably. In a standalone WEB PWA we trust window.screen.height (the stable
 * full-screen height in CSS px); in the browser and in native shells we use
 * window.innerHeight (tracks the URL bar / the real WebView viewport).
 * Updated on resize/orientation so it always equals the live viewport.
 */
function setAppHeight(): void {
  if (typeof window === 'undefined' || !document.documentElement) return;
  // Native shells are excluded: their WebView reports innerHeight correctly, and
  // screen.height is orientation-independent — in landscape it stays the PORTRAIT
  // height, over-sizing #root and leaving a blank band beside the content.
  const standalone =
    !Capacitor.isNativePlatform() &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true);
  const h = standalone && window.screen?.height ? window.screen.height : window.innerHeight;
  document.documentElement.style.setProperty('--app-height', `${h}px`);
}
setAppHeight();
window.addEventListener('resize', setAppHeight);
window.addEventListener('orientationchange', setAppHeight);

/**
 * Register the PWA service worker — WEB ONLY. The native (Capacitor) app serves
 * from a custom scheme and manages its own lifecycle, so a SW there is both
 * unnecessary and risky (it would intercept the app shell). On the web it gives
 * the deployed build an active SW (a Chrome/Android installability signal); the
 * SW itself is a no-cache pass-through (see vite.config SERVICE_WORKER_SOURCE).
 * Builds without PWA assets simply 404 on /sw.js and the catch swallows it.
 */
if (!Capacitor.isNativePlatform() && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* no PWA in this build */ });
  });
}

/**
 * Splash-gated boot:
 *   1. Run bootstrap() — domain resolve, branding, auth restore, etc.
 *   2. Render <App/>; <App/> hides the static splash element once mounted.
 *
 * Errors during bootstrap are caught and surfaced via a fallback splash
 * with retry — bootstrap.ts itself handles user-facing recovery flows.
 */
bootstrap()
  .catch((error) => {
    console.error('[bootstrap] Fatal during initialization:', error);
  })
  .finally(() => {
    const root = document.getElementById('root');
    if (!root) {
      throw new Error('[main] Missing #root in index.html');
    }

    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    );
  });
